import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, userService } from '../services/supabase';
import { useNavigate, useLocation } from 'react-router-dom';

type User = {
  id: string;
  nome: string;
  email: string;
  avatar_url: string | null;
  tipo_usuario: 'admin' | 'comum';
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<{ error: any | null }>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Função para verificar o usuário atual
  const checkUser = async () => {
    try {
      setLoading(true);
      const { data, error } = await authService.getCurrentUser();
      
      if (error || !data.user) {
        setUser(null);
        setIsAdmin(false);
        
        // Se não estiver na página de login, redirecionar
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
        
        setLoading(false);
        return;
      }
      
      // Busca detalhes adicionais do usuário
      const { data: userData, error: userError } = await userService.getUserById(data.user.id);
      
      if (userError || !userData) {
        setUser(null);
        setIsAdmin(false);
        
        // Se não estiver na página de login, redirecionar
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
        
        setLoading(false);
        return;
      }
      
      setUser(userData);
      setIsAdmin(userData.tipo_usuario === 'admin');
      
      // Se estiver na página de login e já estiver autenticado, redirecionar
      if (location.pathname === '/login' || location.pathname === '/') {
        if (userData.tipo_usuario === 'admin') {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/home', { replace: true });
        }
      }
    } catch (err) {
      console.error('Erro ao verificar usuário:', err);
      setUser(null);
      setIsAdmin(false);
      
      // Se não estiver na página de login, redirecionar
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Verificar usuário na inicialização e quando a rota mudar
  useEffect(() => {
    checkUser();
  }, [location.pathname]);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { data, error } = await authService.login(email, password);
      
      if (error || !data.user) {
        return { error: error || new Error('Erro ao fazer login') };
      }
      
      // Busca detalhes adicionais do usuário
      const { data: userData, error: userError } = await userService.getUserById(data.user.id);
      
      if (userError || !userData) {
        return { error: userError || new Error('Erro ao buscar dados do usuário') };
      }
      
      setUser(userData);
      setIsAdmin(userData.tipo_usuario === 'admin');
      
      // Redireciona com base no tipo de usuário
      if (userData.tipo_usuario === 'admin') {
        navigate('/dashboard');
      } else {
        navigate('/home');
      }
      
      return { error: null };
    } catch (err: any) {
      return { error: err };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await authService.logout();
      setUser(null);
      setIsAdmin(false);
      navigate('/login');
    } catch (err) {
      console.error('Erro ao fazer logout:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAdmin, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}; 